import { ActionTree } from "vuex";
import { Cookies } from "quasar";
import * as Sentry from "@sentry/vue";
import { StateInterface } from "../index";
import { UserStateInterface } from "./state";
import API from "src/services/api/api";
import maObjects from "src/modules/ma/classes/ma_objects";
import MaRefs from "src/modules/ma/classes/ma_refs";

const actions: ActionTree<UserStateInterface, StateInterface> = {
  async init(context, data: any): Promise<void> {
    const setStateByKey = (key: string, value: any) =>
      context.commit("setStateByKey", { key, value });
    const user = data.user;
    setStateByKey("ref", maObjects.toRef(user));
    setStateByKey("id", maObjects.ref2str(context.state.ref));
    setStateByKey("name", user.name);
    setStateByKey("acl", data.acl);
    setStateByKey("logins", [...context.state.logins, context.state.id]);
    if (data.is_anonym) {
      Cookies.remove("ma_session");
      setStateByKey("ma_session", null);
    }

    if (context.getters["isAnonymous"]) {
      setStateByKey("inited", false);
    } else {
      await context.dispatch("makeAccess", user.items.roles);
      await context.dispatch("makeAddLogins", user.items.addLogins);
      await context.dispatch("makeClassesRools", user.items.classesRools);
      await context.dispatch(
        "makeMenuRef",
        user.items.user_const || data.const,
      );
      setStateByKey("inited", true);
    }

    return Promise.resolve();
  },

  async makeAccess(context, roles: any[]): Promise<void> {
    const setStateByKey = (key: string, value: any) =>
      context.commit("setStateByKey", { key, value });
    const rs = await maObjects.getObjects(roles);
    const access: { [key: string]: boolean } = { "": true };
    rs.forEach((r) =>
      r.items.access.forEach((a: any) => (access[maObjects.ref2str(a)] = true)),
    );
    setStateByKey("access", access);
    return Promise.resolve();
  },

  async makeAddLogins(context, addLogins: any[]): Promise<void> {
    const setStateByKey = (key: string, value: any) =>
      context.commit("setStateByKey", { key, value });
    if (addLogins) {
      try {
        const logins = (await maObjects.getObjects(addLogins)).map(
          (x) => x.name,
        );
        setStateByKey("logins", [...context.state.logins, ...logins]);
      } catch (error) {
        (window as any).showError(`${error}: Доп. логины`);
        Sentry.captureException(error);
      }
    }
    return Promise.resolve();
  },

  async makeClassesRools(context, classesRools: any[]): Promise<void> {
    const setStateByKey = (key: string, value: any) =>
      context.commit("setStateByKey", { key, value });
    if (classesRools) {
      try {
        const objs = await maObjects.getObject(classesRools);
        const rools = objs.items.rools
          .split(/\s+/)
          .map((r: any) => parseInt(r))
          .filter((x: any) => x);
        const is_exc = objs.items.isExc;
        setStateByKey("classesRools", {
          rools,
          is_exc,
          exists: rools.length || is_exc,
        });
      } catch (error) {
        (window as any).showError(`${error}: Правила классов`);
        Sentry.captureException(error);
      }
    }
    return Promise.resolve();
  },

  async makeMenuRef(context, consts: any): Promise<void> {
    const setStateByKey = (key: string, value: any) =>
      context.commit("setStateByKey", { key, value });
    const obj = await maObjects.getObject(consts);
    setStateByKey("menuRef", obj.items.client_menu_wf);
    return Promise.resolve();
  },

  async signin(
    context,
    data: { login: string; password: string },
  ): Promise<string> {
    const ma_session = await API.wfAuth.signin(data.login, data.password);

    return context.dispatch("afterSignin", { ma_session });
  },

  afterSignin(context, data: { ma_session: string }): string {
    const setStateByKey = (key: string, value: any) =>
      context.commit("setStateByKey", { key, value });
    if (data.ma_session) {
      setStateByKey("ma_session", data.ma_session);
      Cookies.set("ma_session", context.state.ma_session ?? "", {
        path: "/",
        sameSite: "None; Secure", // для работы в iframe
      });
    }

    return data.ma_session;
  },

  async signout(context): Promise<void> {
    const setStateByKey = (key: string, value: any) =>
      context.commit("setStateByKey", { key, value });
    API.wfAuth.signout(context.state.ma_session);
    Cookies.remove("ma_session");
    setStateByKey("ma_session", undefined);
    setStateByKey("inited", false);
    return Promise.resolve();
  },

  async getNameForLogin(context, login: string): Promise<string> {
    if (login?.includes("ma_user")) {
      const ref = maObjects.str2ref(login);
      const obj = await maObjects.getObject(ref);
      return Promise.resolve<string>(obj.name);
    } else {
      return Promise.resolve<string>(login);
    }
  },

  // Facebook

  async signin_with_facebook(
    context,
    data: { accessToken: string },
  ): Promise<string> {
    const response = await API.cfAuth.signin_with_facebook(data.accessToken);
    const ma_session = response?.ma_session;
    await context.dispatch("afterSignin", { ma_session });

    return ma_session;
  },

  async attach_facebook(
    context,
    data: { accessToken: string },
  ): Promise<boolean> {
    try {
      await API.cfAuth.fb_user_attach(data.accessToken);
      return true;
    } catch {
      return false;
    }
  },
};

export default actions;
